import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import PINEntryModal from '../../../components/ui/PINEntryModal';
import { isLockEnabled, setupLock, disableLock, validatePIN, isSessionUnlocked, lockSession } from '../../../utils/parentalLockService';

const ParentalLockSettings = () => {
  const [lockEnabled, setLockEnabled] = useState(isLockEnabled());
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showChangePINModal, setShowChangePINModal] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: enter PIN, 2: confirm PIN
  const [firstPIN, setFirstPIN] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSetupPIN = async (pin) => {
    if (setupStep === 1) {
      setFirstPIN(pin);
      setSetupStep(2);
      return true; // Continue to confirmation
    }

    // Step 2: Confirm PIN
    if (pin !== firstPIN) {
      setError('PINs do not match. Please try again.');
      setSetupStep(1);
      setFirstPIN('');
      return false;
    }

    try {
      setupLock(pin);
      setLockEnabled(true);
      setShowSetupModal(false);
      setSetupStep(1);
      setFirstPIN('');
      setSuccess('Parental lock enabled successfully!');
      setTimeout(() => setSuccess(''), 3000);
      return true;
    } catch (err) {
      setError(err?.message || 'Failed to setup lock');
      return false;
    }
  };

  const handleDisableLock = async (pin) => {
    const isValid = validatePIN(pin);
    
    if (!isValid) {
      return false;
    }

    disableLock();
    setLockEnabled(false);
    setShowDisableModal(false);
    setSuccess('Parental lock disabled successfully!');
    setTimeout(() => setSuccess(''), 3000);
    return true;
  };

  const handleLockNow = () => {
    lockSession();
    setSuccess('Session locked. Protected content now requires PIN.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const closeSetupModal = () => {
    setShowSetupModal(false);
    setSetupStep(1);
    setFirstPIN('');
    setError('');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
          <Icon name="shield" size={24} className="text-warning" />
        </div>
        <div className="flex-1">
          <h2 className="text-h3 font-heading text-foreground mb-2">
            Parental Lock
          </h2>
          <p className="text-sm text-muted-foreground">
            Protect sensitive settings and operations with a 4-digit PIN. When enabled, editing tasks, changing settings, and managing family members will require PIN authentication.
          </p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
          <Icon name="check-circle" size={20} className="text-success" />
          <span className="text-success font-medium">{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
          <Icon name="alert-circle" size={20} className="text-destructive" />
          <span className="text-destructive font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Lock Status */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon 
              name={lockEnabled ? "lock" : "unlock"} 
              size={20} 
              className={lockEnabled ? "text-warning" : "text-muted-foreground"} 
            />
            <div>
              <p className="font-medium text-foreground">
                {lockEnabled ? 'Lock Enabled' : 'Lock Disabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {lockEnabled 
                  ? 'Protected content requires PIN authentication' :'All content is accessible without PIN'}
              </p>
            </div>
          </div>
          <div className={`w-3 h-3 rounded-full ${lockEnabled ? 'bg-success' : 'bg-muted-foreground'}`} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {!lockEnabled ? (
            <Button
              variant="default"
              iconName="lock"
              onClick={() => setShowSetupModal(true)}
            >
              Enable Parental Lock
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                iconName="lock"
                onClick={handleLockNow}
                disabled={!isSessionUnlocked()}
              >
                Lock Now
              </Button>
              <Button
                variant="destructive"
                iconName="unlock"
                onClick={() => setShowDisableModal(true)}
              >
                Disable Lock
              </Button>
            </>
          )}
        </div>

        {/* Protected Features List */}
        {lockEnabled && (
          <div className="mt-6 p-4 border border-border rounded-lg">
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Icon name="shield-check" size={18} className="text-primary" />
              Protected Features
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Icon name="check" size={14} className="text-success" />
                Family member deletion and editing
              </li>
              <li className="flex items-center gap-2">
                <Icon name="check" size={14} className="text-success" />
                Global preferences and settings changes
              </li>
              <li className="flex items-center gap-2">
                <Icon name="check" size={14} className="text-success" />
                Calendar sync configuration
              </li>
              <li className="flex items-center gap-2">
                <Icon name="check" size={14} className="text-success" />
                Backup and restore operations
              </li>
              <li className="flex items-center gap-2">
                <Icon name="check" size={14} className="text-success" />
                Task editing and deletion
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <PINEntryModal
          title={setupStep === 1 ? 'Create PIN' : 'Confirm PIN'}
          description={
            setupStep === 1 
              ? 'Enter a 4-digit PIN to protect sensitive settings and operations' :'Re-enter your PIN to confirm'
          }
          onClose={closeSetupModal}
          onSubmit={handleSetupPIN}
          submitLabel={setupStep === 1 ? 'Continue' : 'Enable Lock'}
        />
      )}

      {/* Disable Modal */}
      {showDisableModal && (
        <PINEntryModal
          title="Disable Parental Lock"
          description="Enter your PIN to disable parental lock protection"
          onClose={() => setShowDisableModal(false)}
          onSubmit={handleDisableLock}
          submitLabel="Disable Lock"
        />
      )}
    </div>
  );
};

export default ParentalLockSettings;
